import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  asapScheduler,
  debounceTime,
  fromEvent,
  Observable,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';

type InputValueType = string | number;

@Component({
  selector: 'app-input-color-box',
  templateUrl: 'input-color-box.component.html',
  styleUrls: ['input-color-box.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: InputColorBoxComponent,
    },
  ],
})
export class InputColorBoxComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('inputEl', { static: true })
  inputElRef: ElementRef<HTMLInputElement>;

  private formEl: HTMLFormElement;
  private formResetSubscription: Subscription;

  @Input()
  value: any;
  @Output()
  valueChange = new EventEmitter<any>();

  formControl: FormControl<string | number>;

  @Output()
  onReset = new EventEmitter<any>();
  @Output()
  onInput = new EventEmitter<any>();
  @Output()
  onFocus = new EventEmitter<any>();

  @Output()
  onClearClick = new EventEmitter<any>();

  private onChange = (value) => {};

  private onTouched = () => {};

  @Input()
  name: string;

  @Input()
  readOnly: boolean;

  @Input()
  get disabled() {
    return !!this.formControl.disabled;
  }

  set disabled(value) {
    value = !!value;
    if (value === this.disabled) {
      return;
    }

    if (value) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }

    this.changeDetector.detectChanges();
  }

  @Input()
  showErrorEnabled: boolean = true;
  @Input()
  showClearButton: boolean = true;

  private _focused = false;
  get focused() {
    return this._focused;
  }

  set focused(value) {
    value = !!value;
    if (value !== this.focused) {
      this._focused = value;

      this.changeDetector.detectChanges();
    }
  }

  touched = false;

  get empty() {
    return !this.inputElRef?.nativeElement?.value && !this._isBadInput();
  }

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private changeDetector: ChangeDetectorRef
  ) {
    this.formControl = this.formBuilder.control(null);
  }

  ngOnInit(): void {
    let parentEl = this.inputElRef.nativeElement.parentElement;
    while (parentEl && !(parentEl instanceof HTMLFormElement)) {
      parentEl = parentEl.parentElement;
    }

    if (parentEl instanceof HTMLFormElement) {
      this.formEl = parentEl;
      fromEvent(this.formEl, 'reset')
        .pipe(debounceTime(0, asapScheduler), takeUntil(this.lifecycle))
        .subscribe(() => {
          this.onInputChange();

          this.onReset.emit();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this._setInputValue();
    }
  }

  ngAfterViewInit(): void {
    this._setInputValue();
  }

  ngOnDestroy() {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  writeValue(value: InputValueType): void {
    if (this.value !== value) {
      this.value = value;
      this._setInputValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFocusChanged(focus) {
    this.focused = focus;

    this.onFocus.emit(focus);
  }

  onInputChange() {
    this.formResetSubscription?.unsubscribe();

    let value = this._getValueFromInput();

    if (this.value !== value) {
      this.value = value;

      this.valueChange.emit(value);
      this.onChange?.(value);
    }
  }

  onInputValue(event) {
    this.onInputChange();

    this.onInput.emit({ event, component: this });
  }

  onBtnClearClick(e) {
    e?.stopPropagation();
    this.onClearClick.emit();

    this.formControl.reset();
    this.onInputChange();
  }

  @HostListener('click')
  onContainerClick() {
    if (!this.focused && !this.disabled && !this.readOnly) {
      this.focused = true;
      this.inputElRef.nativeElement.focus();

      if (!this.touched) {
        this.touched = true;
        this.onTouched?.();
      }
    }
  }

  protected _isBadInput() {
    // The `validity` property won't be present on platform-server.
    let validity = (this.inputElRef?.nativeElement as HTMLInputElement)
      ?.validity;
    return validity && validity.badInput;
  }

  private _getValueFromInput() {
    return this.formControl.getRawValue();
  }

  private _setInputValue() {
    this.formControl.reset(this.value);

    this.changeDetector.detectChanges();
  }
}
